"use client";

import { useEffect, useCallback, type RefObject } from "react";
import { stepsUtilData } from "@/lib/utils";
import { ERROR_PAGE_ROUTE, URLS } from "@/constants/constants";
import { Actions, State } from "@/app/context/AppProvider/reducer";
import { setCancelTrigger, setFormUnsaved, setPendingRoute, toggleModal } from "@/app/context/AppProvider/actions";
import { useUnsavedChangesStore } from "@/app/store/useUnsavedChangesStore";

type Params = {
  state: State;
  dispatch: React.Dispatch<Actions>;
  cancelTriggeredRef: RefObject<boolean>;
};

export const useNavigationGuards = ({ state, dispatch, cancelTriggeredRef }: Params) => {
  const isFormDirty = useUnsavedChangesStore((s) => s.isFormDirty);
  const refinementInProgress = useUnsavedChangesStore((s) => s.refinementInProgress);
  const isCancelWithoutSavingClicked = useUnsavedChangesStore((s) => s.isCancelWithoutSavingClicked);
  const setLinkText = useUnsavedChangesStore((s) => s.setLinkText);
  const isBlocking = Boolean(state.isFormUnsaved || cancelTriggeredRef.current || isFormDirty || refinementInProgress);
  const openModal = useUnsavedChangesStore((s) => s.openModal);
  const setPendingRoutes = useUnsavedChangesStore((s) => s.setPendingRoutes);
  const setFormDirty = useUnsavedChangesStore((state) => state.setFormDirty);

  const openGuard = useCallback(
    (nextUrl: string) => {
      if (isFormDirty || refinementInProgress || isCancelWithoutSavingClicked) {
        setPendingRoutes(nextUrl);
        openModal(true);
      } else {
        dispatch(setPendingRoute(nextUrl));
        dispatch(toggleModal(true));
        dispatch(setCancelTrigger(false));
      }
    },
    [dispatch, isFormDirty, refinementInProgress, isCancelWithoutSavingClicked],
  );

  useEffect(() => {
    dispatch(setFormUnsaved(false));
    setFormDirty(false);
  }, [dispatch, setFormDirty]);

  useEffect(() => {
    if (isCancelWithoutSavingClicked) {
      openGuard(URLS.HOME);
    }
  }, [isCancelWithoutSavingClicked]);

  useEffect(() => {
    if (state?.cancelTrigger) {
      openGuard(URLS.HOME);
    }
  }, [dispatch, openGuard, state?.cancelTrigger]);

  useEffect(() => {
    const onClickCapture = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0) return;

      const target = e.target as HTMLElement | null;
      const anchor = target?.closest?.("a") as HTMLAnchorElement | null;
      if (!anchor) return;

      const href = anchor.getAttribute("href");
      if (!href || href.startsWith("#")) return;
      if (anchor.target === "_blank" || anchor.hasAttribute("download")) return;
      const text = anchor.textContent?.trim() ?? "";
      setLinkText(text);

      const isExternal = anchor.origin !== globalThis.location.origin;
      if (isExternal) return;

      if (isBlocking && href !== ERROR_PAGE_ROUTE) {
        e.preventDefault();
        openGuard(href);
      }
    };

    document.addEventListener("click", onClickCapture, true);
    return () => document.removeEventListener("click", onClickCapture, true);
  }, [isBlocking, openGuard]);

  useEffect(() => {
    if (stepsUtilData.isSavedProject) return;

    const handleBeforeUnload = (event: BeforeUnloadEvent) => {
      if (state.isFormUnsaved || isFormDirty) {
        event.preventDefault();
        return "";
      }
    };

    globalThis.addEventListener("beforeunload", handleBeforeUnload);
    return () => globalThis.removeEventListener("beforeunload", handleBeforeUnload);
  }, [state.isFormUnsaved, isFormDirty]);

  useEffect(() => {
    let lastPathname = location.pathname;
    const handlePopState = () => {
      if (!isBlocking) return;
      // If only the hash changed (skip-to-content, anchor links), ignore it
      if (location.pathname === lastPathname) {
        lastPathname = location.pathname;
        return;
      }
      openGuard(document.referrer || "/");
      history.pushState(null, "", location.href);
    };

    globalThis.addEventListener("popstate", handlePopState);
    return () => globalThis.removeEventListener("popstate", handlePopState);
  }, [isBlocking, openGuard]);
};
